import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import PageHead from "@/components/PageHead";

/* Editorial blog listing — clean, generous whitespace, photography-first.
   Matches the approved visual reference: large black titles, tiny copper
   category eyebrow, comfortable reading rhythm.

   Pagination: fetches PAGE_SIZE posts at a time; a "Carregar mais" button
   pulls the next batch. Stays fast and unobtrusive as the archive grows. */

const PAGE_SIZE = 9;

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("pt-PT", { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
};

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        api.get("/blog", { params: { limit: PAGE_SIZE, skip: 0 } })
            .then((r) => {
                const payload = r.data || {};
                // Back-compat: older builds returned a bare array
                const items = Array.isArray(payload) ? payload : (payload.items || []);
                const totalCount = Array.isArray(payload) ? payload.length : (payload.total ?? items.length);
                setPosts(items);
                setTotal(totalCount);
            })
            .finally(() => setLoading(false));
    }, []);

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const r = await api.get("/blog", { params: { limit: PAGE_SIZE, skip: posts.length } });
            const payload = r.data || {};
            const items = Array.isArray(payload) ? payload : (payload.items || []);
            const totalCount = Array.isArray(payload) ? posts.length + items.length : (payload.total ?? posts.length + items.length);
            setPosts((prev) => [...prev, ...items]);
            setTotal(totalCount);
        } finally { setLoadingMore(false); }
    };

    const featured = posts[0];
    const rest = posts.slice(1);
    const hasMore = posts.length < total;

    return (
        <>
            <PageHead
                title="Blog — O Homem da Obra"
                description="Artigos sobre remodelações, gestão de obra e valorização de imóveis. Dicas práticas, guias e histórias de obra."
                canonical="https://ohomemdaobra.pt/blog"
            />

            {/* Intro editorial */}
            <section className="pt-16 sm:pt-24 pb-8 sm:pb-12">
                <div className="max-w-3xl mx-auto px-6 sm:px-10">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-5">
                        — Blog
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-stone-900 tracking-tight leading-[1.02]" data-testid="blog-title">
                        Artigos & histórias de obra.
                    </h1>
                    <p className="text-stone-600 text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed">
                        Guias práticos, dicas e reflexões sobre remodelação, gestão de obra e valorização de imóveis.
                    </p>
                </div>
            </section>

            {/* Featured article */}
            {featured && (
                <section className="pb-14 sm:pb-20">
                    <div className="max-w-5xl mx-auto px-6 sm:px-10">
                        <Link to={`/blog/${featured.slug}`} className="block group" data-testid={`blog-featured-${featured.slug}`}>
                            {featured.cover_id && (
                                <div className="rounded-2xl sm:rounded-3xl overflow-hidden aspect-[16/9] bg-stone-100 mb-8">
                                    <img src={fileUrl(featured.cover_id)} alt={featured.cover_alt || featured.title}
                                        loading="eager"
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition duration-700" />
                                </div>
                            )}
                            <div className="max-w-3xl">
                                {featured.category && (
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-4">
                                        — {featured.category}
                                    </div>
                                )}
                                <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.05] group-hover:opacity-80 transition break-words">
                                    {featured.title}
                                </h2>
                                {(featured.subtitle || featured.excerpt) && (
                                    <p className="text-stone-600 text-lg mt-5 leading-relaxed max-w-2xl">
                                        {featured.subtitle || featured.excerpt}
                                    </p>
                                )}
                                <div className="mt-6 text-xs uppercase tracking-wider text-stone-500 flex items-center gap-3">
                                    <span>{formatDate(featured.published_at)}</span>
                                    {featured.reading_time_mins ? (
                                        <>
                                            <span aria-hidden>|</span>
                                            <span>{featured.reading_time_mins} min de leitura</span>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            {/* Rest of the articles — 2 columns on desktop, 1 on mobile */}
            {rest.length > 0 && (
                <section className="pb-14 sm:pb-20 border-t border-stone-200 pt-14 sm:pt-20">
                    <div className="max-w-5xl mx-auto px-6 sm:px-10">
                        <div className="grid sm:grid-cols-2 gap-10 sm:gap-14" data-testid="blog-grid">
                            {rest.map((p) => (
                                <Link key={p.id} to={`/blog/${p.slug}`} className="block group" data-testid={`blog-card-${p.slug}`}>
                                    {p.cover_id ? (
                                        <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-stone-100 mb-6">
                                            <img src={fileUrl(p.cover_id)} alt={p.cover_alt || p.title} loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-700" />
                                        </div>
                                    ) : (
                                        <div className="rounded-2xl aspect-[16/10] bg-stone-100 mb-6" />
                                    )}
                                    {p.category && (
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-3">
                                            — {p.category}
                                        </div>
                                    )}
                                    <h3 className="font-display text-2xl sm:text-3xl font-black text-stone-900 tracking-tight leading-[1.1] group-hover:opacity-80 transition break-words">
                                        {p.title}
                                    </h3>
                                    {(p.subtitle || p.excerpt) && (
                                        <p className="text-stone-600 text-base mt-3 leading-relaxed line-clamp-3">
                                            {p.subtitle || p.excerpt}
                                        </p>
                                    )}
                                    <div className="mt-4 text-xs uppercase tracking-wider text-stone-500 flex items-center gap-3">
                                        <span>{formatDate(p.published_at)}</span>
                                        {p.reading_time_mins ? (
                                            <>
                                                <span aria-hidden>|</span>
                                                <span>{p.reading_time_mins} min</span>
                                            </>
                                        ) : null}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Load more */}
            {hasMore && (
                <section className="pb-24 sm:pb-32">
                    <div className="max-w-5xl mx-auto px-6 sm:px-10 flex justify-center">
                        <button
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="text-[11px] uppercase tracking-[0.22em] font-bold border-b-2 border-[hsl(var(--copper))] pb-1 text-stone-900 hover:opacity-70 transition disabled:opacity-40"
                            data-testid="blog-load-more"
                        >
                            {loadingMore ? "A carregar…" : "Carregar mais artigos"}
                        </button>
                    </div>
                </section>
            )}
            {!hasMore && posts.length > 0 && (
                <div className="pb-24 sm:pb-32" />
            )}

            {!loading && posts.length === 0 && (
                <section className="pb-24">
                    <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
                        <p className="text-stone-500">Ainda sem artigos publicados. Volte em breve.</p>
                    </div>
                </section>
            )}
        </>
    );
}
